/**
 * Приход случился — исполнить назначенное.
 *
 * Тихо закрепить деньги «потому что так было в плане» нельзя. Назначение
 * делалось заранее, а пришло могло меньше, и планы к этому дню могли стать
 * другими. Поэтому приложение сохраняет факт, пересчитывает картину и
 * **предлагает** исполнение — решает человек, по каждому назначению отдельно.
 *
 * Здесь же заканчивается разделение трёх состояний денег: после подтверждения
 * назначение перестаёт быть обещанием и становится обычным конвертом. До
 * подтверждения оно остаётся планом, даже если дата давно прошла.
 */
import { useState } from 'react';
import { ZERO, add, formatMoney, minM, type Money, type PlainDate } from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { allocationLabel } from './plan-income.js';
import {
  cancelAllocation, executeAllocation, type AppState,
} from './store.js';

const MONTHS = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
const formatDate = (d: string): string => {
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]}`;
};

export interface FulfilTarget {
  incomeStreamId: string;
  /** Дата записанного прихода. */
  date: PlainDate;
  /** Счёт, на который деньги пришли: конверт принадлежит счёту. */
  accountId: string;
  /** Сколько пришло на самом деле. */
  arrived: Money;
}

export function FulfilAllocations({ state, target, onClose, onDone }: {
  state: AppState;
  target: FulfilTarget;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const waiting = state.plannedAllocations.filter(
    (a) => (a.status ?? 'planned') === 'planned'
      && a.incomeStreamId === target.incomeStreamId
      && a.expectedDate <= target.date,
  );
  const promised = add(...waiting.map((a) => a.amount));
  // Пришло меньше обещанного — обычное дело, и молча урезать назначения
  // нельзя: какое из них сократить, знает только человек.
  const short = promised > target.arrived;

  const labelOf = ({ target }: typeof waiting[number]): string => allocationLabel(state, target);

  if (waiting.length === 0) return null;

  return (
    <SheetWrap title="Приход записан" onClose={onClose}
      tip={<>«Не сейчас» ничего не теряет: назначения останутся на главной и будут
        ждать решения. Пока они не закреплены, эти деньги считаются свободными —
        приложение не станет держать их взаперти само.</>}>
      <div className="form">
        <div className="note">
          Пришло {formatMoney(target.arrived)} — {formatDate(target.date)}. На эти деньги
          заранее назначено {formatMoney(promised)}. Закрепить назначенное?
        </div>
        {short && (
          <div className="warnline">
            Пришло меньше, чем обещано назначениями. Закрепить всё не получится —
            выбери, что важнее: сокращать назначения за тебя приложение не станет.
          </div>
        )}

        {waiting.map((a) => (
          <div className="li acts" key={a.id}>
            <div className="nm">
              <b>{labelOf(a)}</b>
              <span>назначено {formatMoney(a.amount)} · ждали {formatDate(a.expectedDate)}</span>
            </div>
            <button className="btn inline pri" disabled={busy}
              onClick={async () => {
                setBusy(true);
                await executeAllocation({
                  id: a.id,
                  accountId: target.accountId,
                  amount: minM(a.amount, target.arrived),
                  cycleStart: target.date,
                });
                onDone();
              }}>
              Закрепить
            </button>
            <button className="btn inline" disabled={busy}
              onClick={async () => { setBusy(true); await cancelAllocation(a.id); onDone(); }}>
              Отменить
            </button>
          </div>
        ))}

        <button className="btn" disabled={busy} onClick={onClose}>Не сейчас</button>
      </div>
    </SheetWrap>
  );
}

/** Сколько ещё ждёт исполнения по этому источнику. */
export const waitingFor = (
  state: AppState, incomeStreamId: string, date: PlainDate,
): Money => add(...state.plannedAllocations
  .filter((a) => (a.status ?? 'planned') === 'planned'
    && a.incomeStreamId === incomeStreamId && a.expectedDate <= date)
  .map((a) => a.amount)) || ZERO;
